/**
 * Number formatting code based on accounting.js v0.4.2
 * Copyright 2014 Open Exchange Rates
 *
 * Freely distributable under the MIT license.
 * Portions of accounting.js are inspired or borrowed from underscore.js
 */


/**
 * Formatting number according to options given. Options must contain the following attributes:
 * * usePrecision - use decimal precision or not (true/false)
 * * precision - number of significant digits
 * * thousandSep - the separator to use between thousands of digits
 * * decimalSep - the decimal separator to use
 *
 * @param rawNumber
 * @param opts
 * @returns {string}
 */
export function format (rawNumber, opts) {
  //console.debug("FormatNumber with options", rawNumber, opts);

  const number = unformat(rawNumber);
  const precision = Number.isFinite(opts.precision) && opts.precision >= 0 ? opts.precision : 0;
  const negative = number < 0 ? "-" : "",
    base = Number.parseInt(toFixed(Math.abs(number || 0), precision), 10) + "",
    mod = base.length > 3 ? base.length % 3 : 0;

  const decimalPart = opts.usePrecision ? toFixed(Math.abs(number), precision).split('.')[1] ?? "" : "";

  return negative
    + (mod ? base.slice(0, mod) + opts.thousandSep : "")
    + base.slice(mod).replace(/(\d{3})(?=\d)/g, "$1" + opts.thousandSep)
    + (opts.usePrecision && decimalPart ? opts.decimalSep + decimalPart : "");
}

/**
 * Som format(), men faller tilbake til String(rawNumber) hvis formateringen
 * feiler. Brukes av visningslag som ikke selv skal eie try/catch (WI 1871).
 */
export function formatSafe (rawNumber, opts) {
  try {
    return format(rawNumber, opts);
  } catch (error) {
    console.error('Feil ved formatering av tall:', error, rawNumber);
    return String(rawNumber);
  }
}

/**
 * Implementation of toFixed() that treats floats more like decimals
 *
 * Fixes binary rounding issues (eg. (0.615).toFixed(2) === "0.61") that present
 * problems for accounting- and finance-related software.
 */
function toFixed (value, precision) {
  const exponentialForm = Number(value + 'e' + precision);
  const rounded = Math.round(exponentialForm);
  return Number(rounded + 'e-' + precision).toFixed(precision);
}

/**
 * Takes a string, removes all formatting/cruft and returns the raw float value
 *
 * Decimal must be included in the regular expression to match floats, if the number uses a non-standard decimal
 * separator, provide it as the second argument.
 *
 * Also matches bracketed negatives (eg. "$ (1.99)" => -1.99)
 *
 * Doesn't throw any errors (`NaN`s become 0) but this may change in future
 */
function unformat (value, decimal = '.') {
  // Empty or non-numeric input ('' / null / undefined / NaN) means 0 by the formatting
  // contract. A default parameter only covers undefined (and would let NaN through the
  // number fast-path below), so the explicit fallback stays.
  const raw = value || 0;

  // Return the value as-is if it's already a number:
  if (typeof raw === "number") return raw;

  // Build regex to strip out everything except digits, decimal point and minus sign:
  const regex = new RegExp("[^0-9-" + decimal + "]", ["g"]),
    unformatted = Number.parseFloat(
      ("" + raw)
        .replace(/\((?=\d+)(.*)\)/, "-$1") // replace bracketed values with negatives
        .replace(regex, '')         // strip out any cruft
        .replace(decimal, '.')      // make sure decimal point is standard
    );

  // This will fail silently which may cause trouble, let's wait and see:
  return !Number.isNaN(unformatted) ? unformatted : 0;
}
